import { CreativeListOption } from "@/components/Form/CustomAutocompleteWithImageButtonList";
import Image from "@/components/Image/ImageSecondary";
import CustomImageLoader from "@/components/Loader/CustomImageLoader";
import { useGetBwCreativeDetails } from "@/modules/Asset/Creative/asset.hook";
import { CreativeByChannel } from "@/modules/Asset/Creative/asset.interface";
import { PreviewTemplateThumbnail } from "@/modules/Asset/Creative/ListAsset/AssetTableRow";
import { convertColorFields } from "@/modules/Asset/Template/helper";
import { TemplateVariant } from "@/modules/Asset/Template/template.interface";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import * as React from "react";
const THUMBNAIL_WIDTH = 22;
const THUMBNAIL_HEIGHT = 22;
const CustomListWithImageButtonForAutocomplete = React.forwardRef(
  function CustomListWithImageButtonForAutocomplete(props: any, ref) {
    const { children: options, customProps, ...other } = props;
    const { list, handleClickOpenImage } = customProps as {
      list: CreativeListOption[];
      handleClickOpenImage: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        index: number,
        isTemplateCreative: boolean
      ) => void;
    };
    return (
      <List {...other} ref={ref}>
        {options.map((option: Record<string, any>, index: number) => {
          const {
            key,
            props: { ...optionProp },
            ...rest
          } = option;
          const optionIndex = optionProp["data-option-index"];
          const creatives = list[optionIndex]
            ?.creatives as CreativeByChannel<"display">[];
          const firstCreative = creatives[0];
          const isTemplateCreative = Boolean(
            firstCreative.CreativeTemplateDataValueMapping
              ?.CreativeTemplateVariant
          );
          const { image, urlsLoading } = loadBeeswaxImage(
            firstCreative?.adId,
            isTemplateCreative
          );

          return (
            <ListItem key={index} {...optionProp}>
              <ListItemText primary={key} />
              <IconButton
                sx={{
                  padding: 0,
                  width: "28px",
                  height: "28px",
                  "&:hover": {
                    backgroundColor: "inherit",
                    opacity: "0.7",
                  },
                }}
                onClick={(e) => {
                  handleClickOpenImage(e, optionIndex, isTemplateCreative);
                }}
                disabled={isTemplateCreative ? false : urlsLoading || !image}
              >
                {isTemplateCreative ? (
                  <RenderTemplateAdThumbnail
                    template={
                      firstCreative?.CreativeTemplateDataValueMapping
                        ?.CreativeTemplateVariant
                    }
                    values={
                      firstCreative?.CreativeTemplateDataValueMapping
                        ?.CreativeTemplateDataValues[0]?.fields
                    }
                  />
                ) : (
                  <CustomImageLoader
                    width={THUMBNAIL_WIDTH}
                    height={THUMBNAIL_HEIGHT}
                    isLoading={urlsLoading}
                  >
                    <Image
                      img={image}
                      alt={`${firstCreative.name}_image`}
                      containerHeight={`${THUMBNAIL_HEIGHT}px`}
                      containerWidth={`${THUMBNAIL_WIDTH}px`}
                      errorIcon={BrokenImageOutlinedIcon}
                      containerStyles={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    />
                  </CustomImageLoader>
                )}
              </IconButton>
            </ListItem>
          );
        })}
      </List>
    );
  }
);

export default CustomListWithImageButtonForAutocomplete;

function loadBeeswaxImage(assetId: string | null, isTemplateCreative: boolean) {
  if (!assetId || isTemplateCreative)
    return {
      image: null,
      urlsLoading: false,
    };
  const { data: creativeDetails, isLoading: creativeDetailsLoading } =
    useGetBwCreativeDetails({
      id: assetId,
    });

  return {
    image: creativeDetails?.thumbnailUrl,
    urlsLoading: creativeDetailsLoading,
  };
}

function RenderTemplateAdThumbnail(props: {
  template: TemplateVariant | null | undefined;
  values: Record<string, any> | null | undefined;
}) {
  const { template, values } = props;
  if (!template || !values) return null;
  const templateValues = convertColorFields(values);
  return (
    <PreviewTemplateThumbnail
      template={template as unknown as TemplateVariant}
      isOverlay={true}
      templateValues={templateValues}
      thumbnailWidth={THUMBNAIL_WIDTH}
      thumbnailHeight={THUMBNAIL_HEIGHT}
    />
  );
}
